import { useCallback, useContext, useMemo, Children, isValidElement, useState } from 'react';
import { type Options as MarkdownOptions } from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import styled from '@emotion/styled';
import CheckIcon from '@mui/icons-material/Check';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { IconButton, Tooltip } from '@mui/material';
import { ChatHintType } from '@trinity/chat';
import {
  ActionsContext,
  ActiveChatSessionContext,
  AttachmentItemRendererContext,
} from '@trinity/chat-components/components/Chat/context';
import { useUpdateProfileMutation } from '@trinity/chat-components/components/Chat/KolSearch.Containers';
import { LearnMore } from '@trinity/chat-components/components/Chat/LearnMore';
import { PatientVolumeTooltip } from '@trinity/chat-components/components/presentation/PatientVolumeTooltip';
import { NavigationContext } from '@trinity/chat-components/containers/Chat/RoutingProvider';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import { useHover } from '@trinity/chat-components/hooks/useHover';
import { ChatStateDispatchContext } from '../Chat.State/context';
import { MessageQueryHint, TextQueryHint } from '../presentation/Message.QueryHint';
import { ArrowButton } from './ArrowButton';
import { Citation, Citations } from './Citations';
import { MarkdownBadge } from './MarkdownBadge';
import {
  StyledA,
  StyledBlockQuote,
  StyledH1,
  StyledH2,
  StyledH3,
  StyledH4,
  StyledLi,
  StyledMark,
  StyledOL,
  StyledP,
  StyledStrong,
  StyledTd,
  StyledTh,
  StyledTr,
  StyledUl,
} from './MarkdownElements';
import { MarkdownInfoChip } from './MarkdownInfoChip';
import { ScrollableTable } from './ScrollableTable';

const Spacer = () => <div style={{ height: '10px' }} />;

const CodeBlockWrapper = styled.div`
  position: relative;
  margin: 16px 0;
`;

const CopyButton = styled(IconButton)`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
  background-color: rgba(255, 255, 255, 0.8) !important;
  &:hover {
    background-color: rgba(255, 255, 255, 0.95) !important;
  }
`;

const CodeBlock = ({ language, children }: { language?: string; children: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(children);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // noop
    }
  };

  return (
    <CodeBlockWrapper>
      <Tooltip title={copied ? 'Copied!' : 'Copy code'}>
        <CopyButton onClick={handleCopy} size="small">
          {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
        </CopyButton>
      </Tooltip>
      <SyntaxHighlighter
        language={language || 'text'}
        style={oneLight}
        customStyle={{
          margin: 0,
          borderRadius: '6px',
          fontSize: '14px',
        }}
        codeTagProps={{
          style: {
            fontFamily:
              'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
            fontSize: '14px',
            lineHeight: '1.45',
          },
        }}
      >
        {children}
      </SyntaxHighlighter>
    </CodeBlockWrapper>
  );
};

export function useMarkdownComponents(
  components?: MarkdownOptions['components']
): MarkdownOptions['components'] {
  const AttachmentItem = useContext(AttachmentItemRendererContext);
  const navigate = useContext(NavigationContext);

  return useMemo(
    () => ({
      citations: (props) => {
        const citations = props.node.properties.values as string[];
        return <Citations values={citations} />;
      },
      td: (props) => {
        const content = Children.map(props.children, (c) => {
          if (c === '<br />') {
            return <br />;
          }
          return c;
        });
        return <StyledTd>{content}</StyledTd>;
      },
      p: (props) => {
        // If paragraph contains a block-level custom component, avoid div-in-p DOM nesting
        type HastChild = { type: string; tagName?: string };
        const nodeChildren = (props.node as { children?: HastChild[] })?.children ?? [];
        const hasBlockCustomComponent = nodeChildren.some(
          (child) => child.type === 'element' && child.tagName === 'customComponent'
        );
        if (hasBlockCustomComponent) {
          return <div>{props.children}</div>;
        }

        // Check if paragraph ends with "call plan."
        const text = typeof props.children === 'string' ? props.children : '';
        const hasCallPlan =
          text.includes('call plan.') ||
          (Array.isArray(props.children) &&
            props.children.some((child) => typeof child === 'string' && child.includes('call plan.')));

        if (hasCallPlan) {
          return (
            <>
              <StyledP>{props.children}</StyledP>
              <Spacer />
            </>
          );
        }
        return <StyledP>{props.children}</StyledP>;
      },
      blockquote: ({ children }) => <StyledBlockQuote>{children}</StyledBlockQuote>,
      a: (props) => {
        const href = props.href || '';
        const isInternal = href.startsWith('/') && !href.startsWith('//');
        const kids = Children.toArray(props.children);

        const handleInternalClick = (e: React.MouseEvent) => {
          if (e.metaKey || e.ctrlKey || e.shiftKey) return;
          e.preventDefault();
          navigate?.(href);
        };

        const linkOnClick = isInternal ? handleInternalClick : undefined;
        const linkTarget = isInternal ? undefined : '_blank';
        const linkRel = isInternal ? undefined : 'noopener';

        // Clickable avatar: link wrapping an img
        if (kids.length === 1 && isValidElement(kids[0]) && kids[0].type === 'img') {
          return (
            <a
              href={href}
              onClick={linkOnClick}
              target={linkTarget}
              rel={linkRel}
              style={{ textDecoration: 'none' }}
            >
              {kids[0]}
            </a>
          );
        }
        // Render as button for builder links and key action links
        const childText = getChildText(props.children);
        const isBuilderLink = href.includes('/segmentation?sessionId=');
        const isActionLink =
          /continue in segmentation|view all segments|view cross-segment|explore segments/i.test(childText);
        if (isBuilderLink || isActionLink) {
          return (
            <StyledButtonLink
              href={href}
              onClick={linkOnClick}
              target={linkTarget}
              rel={linkRel}
            >
              {props.children}
            </StyledButtonLink>
          );
        }
        return (
          <StyledA href={href} onClick={linkOnClick} target={linkTarget} rel={linkRel}>
            {props.children}
          </StyledA>
        );
      },
      img: (props) => {
        if (props.src?.includes('avatar-presets')) {
          return (
            <StyledAvatarImg
              src={props.src}
              alt={props.alt || ''}
              onError={(e) => {
                (e.currentTarget).style.display = 'none';
              }}
            />
          );
        }
        return (
          <img
            src={props.src}
            alt={props.alt || ''}
            style={{ maxWidth: '100%' }}
            onError={(e) => {
              (e.currentTarget).style.display = 'none';
            }}
          />
        );
      },
      ul: (props) => <StyledUl>{props.children}</StyledUl>,
      li: (props) => <StyledLi>{props.children}</StyledLi>,
      ol: (props) => <StyledOL>{props.children}</StyledOL>,
      strong: (props) => {
        // Check if this is "Contact History" or "Disclaimer" heading
        const text = typeof props.children === 'string' ? props.children : '';
        if (text === 'Contact History') {
          return (
            <>
              <StyledStrong>{props.children}</StyledStrong>
              <Spacer />
            </>
          );
        }
        if (text === 'Disclaimer') {
          return (
            <>
              <Spacer />
              <StyledStrong>{props.children}</StyledStrong>
            </>
          );
        }
        return <StyledStrong>{props.children}</StyledStrong>;
      },
      h1: (props) => <StyledH1>{props.children}</StyledH1>,
      h2: (props) => <StyledH2>{props.children}</StyledH2>,
      h3: (props) => <StyledH3>{props.children}</StyledH3>,
      h4: (props) => <StyledH4>{props.children}</StyledH4>,
      table: (props) => <ScrollableTable>{props.children}</ScrollableTable>,
      tr: (props) => <StyledTr>{props.children}</StyledTr>,
      th: (props) => <StyledTh>{props.children}</StyledTh>,
      summary: ({ title }) => {
        return <summary>{title}</summary>;
      },
      citation: (props) => {
        const citation = props.node.properties.value as string;
        return <Citation value={citation} />;
      },
      mark: (props) => <StyledMark>{props.children}</StyledMark>,
      pre: (props) => {
        // Return just the children - the code component will handle rendering
        return <>{props.children}</>;
      },
      code: (props) => {
        const { children, className } = props;
        const isInline = !className;

        if (isInline) {
          return (
            <code
              style={{
                backgroundColor: 'rgba(175, 184, 193, 0.2)',
                borderRadius: '6px',
                padding: '0.2em 0.4em',
                fontSize: '85%',
                fontFamily:
                  'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
              }}
            >
              {children}
            </code>
          );
        }

        // Extract language from className (e.g., "language-sql" -> "sql")
        const language = className?.replace('language-', '');
        const codeString = String(children).replace(/\n$/, '');

        return <CodeBlock language={language}>{codeString}</CodeBlock>;
      },
      customComponent: (props) => {
        const componentName = props.node.properties.value as string;
        switch (componentName) {
          case 'patient-volume-tooltip':
            return <PatientVolumeTooltip />;
          default:
            if (/^attachment:/.test(componentName) && AttachmentItem) {
              const [, id] = componentName.split(':');
              return <AttachmentItem id={id} />;
            }

            return null;
        }
      },
      infoChip: ({ node, text, tooltipText, ...props }) => {
        return (
          <MarkdownInfoChip {...props} text={tooltipText} placement="bottom">
            {text}
          </MarkdownInfoChip>
        );
      },
      badge: ({ node, ...props }) => {
        return (
          <>
            <MarkdownBadge {...props} text={props.text} placement="bottom">
              {props.text}
            </MarkdownBadge>
            {!props.inline && <Spacer />}
          </>
        );
      },
      queryhint: ({ node, displayValue, inputValue, append, inline, hintType }) => {
        const shouldAppend = append === 'true';
        if (!inline) {
          return (
            <div style={{ marginTop: 8, marginBottom: 8 }}>
              <QueryHintMDN
                displayValue={displayValue}
                inputValue={inputValue}
                append={shouldAppend}
                hintType={hintType}
              />
            </div>
          );
        } else {
          return <QueryHintInline displayValue={displayValue} inputValue={inputValue} />;
        }
      },
      viewprofile: ({ id, name, identifier, displayValue }) => {
        return <ViewProfile id={id} name={name} identifier={identifier} displayValue={displayValue} />;
      },
      arrowButton: ({ action }) => {
        const actions = useContext(ActionsContext);
        const onClick = useMemo(() => {
          return actions[action] || (() => {});
        }, [actions, action]);
        return <ArrowButton onClick={onClick} />;
      },
      segmentcard: ({ name, subtitle, avatarUrl, url, color }) => {
        const handleClick = (e: React.MouseEvent) => {
          if (e.metaKey || e.ctrlKey || e.shiftKey) return;
          e.preventDefault();
          navigate?.(url);
        };
        return (
          <SegCardRoot href={url} onClick={handleClick} $color={color || '#28aae1'}>
            <SegCardAvatarRing>
              <SegCardAvatarImg src={avatarUrl} alt="" />
            </SegCardAvatarRing>
            <SegCardInfo>
              <SegCardName>{name}</SegCardName>
              <SegCardMeta>{subtitle}</SegCardMeta>
            </SegCardInfo>
            <SegCardChevronCircle>
              <SegCardChevron />
            </SegCardChevronCircle>
          </SegCardRoot>
        );
      },
      ...components,
    }),
    [components, AttachmentItem, navigate]
  );
}

function getChildText(node: React.ReactNode): string {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(getChildText).join('');
  if (isValidElement(node)) {
    const p = node.props as Record<string, unknown>;
    if (p.children) return getChildText(p.children as React.ReactNode);
  }
  return '';
}

const StyledButtonLink = styled.a(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 16px',
  borderRadius: 8,
  border: `1px solid ${theme.palette.primary.main}`,
  background: 'transparent',
  color: theme.palette.primary.main,
  fontFamily: theme.fonts.semiBold,
  fontSize: 14,
  textDecoration: 'none',
  cursor: 'pointer',
  transition: 'all 0.15s',
  marginTop: 4,
  marginBottom: 4,
  '&:hover': {
    background: theme.palette.primary.main,
    color: '#fff',
  },
}));

const StyledAvatarImg = styled.img({
  width: 40,
  height: 40,
  borderRadius: '50%',
  objectFit: 'cover',
  verticalAlign: 'middle',
  marginRight: 8,
  border: '2px solid #e0e0e0',
});

const SegCardChevron = styled(ChevronRightIcon)`
  color: #28aae1;
  font-size: 18px;
  transition:
    transform 0.3s,
    color 0.3s;
`;

const SegCardChevronCircle = styled.div`
  border-radius: 50%;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.3s;
  margin-left: auto;
  flex-shrink: 0;
`;

const SegCardRoot = styled.a<{ $color: string }>`
  background: #fff;
  border: 2px solid #e5e5e5;
  border-left: 4px solid ${(p) => p.$color};
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  text-decoration: none;
  margin-bottom: 8px;
  &:first-of-type {
    margin-top: 5px;
  }
  &:hover {
    background-color: #e5f5fc;
    border-color: ${(p) => p.$color};
  }
  &:hover ${SegCardChevronCircle} {
    background-color: #28aae1;
  }
  &:hover ${SegCardChevron} {
    transform: rotate(90deg);
    color: #ffffff;
  }
`;

const SegCardAvatarRing = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid #28aae1;
  padding: 2px;
  background: white;
  box-sizing: border-box;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const SegCardAvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
  display: block;
`;

const SegCardInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const SegCardName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #323232;
`;

const SegCardMeta = styled.div`
  font-size: 11px;
  color: #626262;
`;

const QueryHintMDN = (props: {
  displayValue: string;
  inputValue: string;
  append?: boolean;
  hintType?: string;
}) => {
  const dispatch = useContext(ChatStateDispatchContext);

  const { displayValue, inputValue, append, hintType } = props;

  const handleHint = useCallback(() => {
    if (append) {
      dispatch({
        type: 'input-appended',
        payload: { value: inputValue },
      });
    } else {
      dispatch({
        type: 'input-changed',
        payload: { value: inputValue },
      });
    }
  }, [dispatch, inputValue, append]);
  return (
    <MessageQueryHint
      hint={{
        type: hintType === 'prompt' ? ChatHintType.Prompt : ChatHintType.Text,
        displayValue,
      }}
      onClick={handleHint}
      disabled={false}
    />
  );
};

const QueryHintInline = (props: { displayValue: string; inputValue: string }) => {
  const [ref, isHovering] = useHover<HTMLLIElement>(!!props.inputValue);

  return (
    <span ref={ref}>
      <LearnMore open={isHovering} displayValue={props.displayValue} question={props.inputValue} />
    </span>
  );
};

type ViewProfileProps = {
  id: number;
  name: string;
  identifier: string;
  displayValue?: string;
};

export const ViewProfile = (props: ViewProfileProps) => {
  const chat = useContext(ActiveChatSessionContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const brandIdentifier = useActiveBrandIdentifier();

  const updateProfileMutation = useUpdateProfileMutation({
    identifier: props.identifier,
  });

  const apiClient = useChatApiClient();

  const handleClick = async () => {
    const search = await apiClient.kolSearch.submitKolSearch({
      chatIdentifier: chat.identifier,
      identifier: props.identifier,
      form: {
        kolId: props.id,
        name: props.name,
      },
      recommendations: {
        status: 'loaded',
        items: [],
        total: 0,
      },
      brandIdentifier,
    });

    dispatch({
      type: 'kol-search-profile-available',
      payload: {
        chatInstance: search.chatInstance,
        result: search.result,
        identifier: props.identifier,
      },
    });

    await updateProfileMutation.mutateAsync({
      kolSearchId: search.result.id,
      kolId: search.result.results[0].id,
    });
  };

  return <TextQueryHint onClick={handleClick} value={props.displayValue} />;
};
